import { Component, Prop, h, Event, EventEmitter, Method } from '@stencil/core';

import type { AntiquaIconColor } from '../../../../types';

import { IFocussable } from 'packages/web-components/src/types/IFocussable';

/**
 * Desktop Icon
 */
@Component({
  tag: 'md-icon-desktop',
  styleUrl: '../md-icons.css',
  shadow: true,
})
export class MdIconDesktop implements IFocussable {
  /**
   * Color of the icon
   */
  @Prop() color: AntiquaIconColor;

  /**
   * Hover color of the icon
   */
  @Prop() colorhover: AntiquaIconColor;

  /**
   * Rotate the icon in the specified direction
   */
  @Prop() rotate:
    | 'up'
    | 'down'
    | 'left'
    | 'right'
    | 'up-right'
    | 'right-down'
    | 'down-left'
    | 'left-up' = 'up';

  /**
   * Animate the icon in the specified direction
   */
  @Prop() animation: 'spin-left' | 'spin-right' | 'none' = 'none';

  /**
   * Animation duration
   */
  @Prop() duration = '2s';

  /**
   * Show title attribute
   */
  @Prop() icontitle: 'enabled' | 'disabled' = 'enabled';

  /**
   * Icon custom alt text attribute
   */
  @Prop() customalttext: string;

  /**
   * Size of the icon
   */
  @Prop() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'sm';

  /**
   * Icon as button or icon
   */
  @Prop() as: 'icon' | 'button' = 'icon';

  /**
   *  Sets the tabidx(tabindex) attribute on the Icon.
   * (setting the value to be -1 removes the icon from the tab order, so users can't focus it using the keyboard).
   * This is only effective if icon uses as prop value to be button.
   */
  @Prop() tabidx = 0;

  /**
   * Ref to the icon element to set focus.
   */
  private iconElement: HTMLMdIconElement;

  /**
   * Icon click handler
   */
  @Event({ eventName: 'handleIconClick' }) handleIconClick: EventEmitter<
    MouseEvent | KeyboardEvent
  >;

  private iconClick = (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation();
    this.handleIconClick.emit(event);
  };

  private handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.iconClick(event);
    }
  };

  @Method()
  async setFocus() {
    const element = this.iconElement?.shadowRoot?.querySelector('span');
    element.focus();
  }

  render() {
    return (
      <md-icon
        alttext="Desktop Icon"
        ref={el => (this.iconElement = el)}
        color={this.color}
        colorhover={this.colorhover}
        rotate={this.rotate}
        animation={this.animation}
        icontitle={this.icontitle}
        duration={this.duration}
        size={this.size}
        exportparts="md-icon"
        customalttext={this.customalttext}
        as={this.as}
        role={this.as === 'button' ? this.as : undefined}
        onClick={this.as === 'button' ? this.iconClick : undefined}
        tabidx={this.as === 'button' ? this.tabidx : null}
        onKeyDown={this.as === 'button' ? this.handleKeyDown : undefined}
      >
        <svg
          aria-hidden="true"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            id="Vector (Stroke) (Stroke)"
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M0.689899 0.689899C1.13163 0.248164 1.73075 0 2.35546 0H13.6445C14.2692 0 14.8684 0.248164 15.3101 0.689899C15.7518 1.13163 16 1.73075 16 2.35546V10.4191C16 11.0438 15.7518 11.6429 15.3101 12.0847C14.8684 12.5264 14.2692 12.7746 13.6445 12.7746H10.7656L11.0898 14.0712L11.7506 14.7321C11.9631 14.9445 12.0266 15.264 11.9116 15.5415C11.7967 15.819 11.5259 16 11.2255 16H4.77455C4.47414 16 4.20331 15.819 4.08835 15.5415C3.97339 15.264 4.03694 14.9445 4.24936 14.7321L4.91025 14.0712L5.23441 12.7746H2.35546C1.73075 12.7746 1.13163 12.5264 0.689899 12.0847C0.248164 11.6429 0 11.0438 0 10.4191V2.35546C0 1.73075 0.248164 1.13163 0.689899 0.689899ZM2.35546 1.48547C2.12473 1.48547 1.90344 1.57713 1.74029 1.74029C1.57713 1.90344 1.48547 2.12473 1.48547 2.35546V8.06363H14.5145V2.35546C14.5145 2.12473 14.4229 1.90344 14.2597 1.74029C14.0966 1.57713 13.8753 1.48547 13.6445 1.48547H2.35546ZM1.48547 9.5491V10.4191C1.48547 10.6498 1.57713 10.8711 1.74029 11.0343C1.90344 11.1974 2.12473 11.2891 2.35546 11.2891H13.6445C13.8753 11.2891 14.0966 11.1974 14.2597 11.0343C14.4229 10.8711 14.5145 10.6498 14.5145 10.4191V9.5491H1.48547ZM9.6694 14.5145L9.23441 12.7746H6.76559L6.3306 14.5145H9.6694Z"
            fill="currentColor"
          />
        </svg>

        <slot name="badge"></slot>
      </md-icon>
    );
  }
}
